/**
 * Pure logic for the chat composer's @-mention picker.
 *
 * No React, no MobX, no DOM. Everything the feature actually promises is
 * specified here and verified in mentionUtils.test.ts before any pixel exists.
 *
 * The problem this solves: t_historical stores practitioner names in
 * inconsistent shapes ("John Smith" but also "Gray, Aaron M."), so a name the
 * user types often fails to match. Letting them pick the EXACT stored string
 * removes the guessing. On send, the picked tokens are rewritten into value
 * lists the agent can filter on directly.
 */

export type MentionCategory = 'practitioner' | 'product' | 'city';

export const MENTION_CATEGORIES: MentionCategory[] = ['practitioner', 'product', 'city'];

/** Values as stored in t_historical, for one @-token. */
export interface Mention {
  id: string;
  /** Locked when the user picks their first value; the other groups then hide. */
  category: MentionCategory;
  /** EXACT stored strings, in selection order. */
  values: string[];
}

/** A mention plus where its token currently sits in the text. */
export interface LocatedMention {
  mention: Mention;
  start: number;
  /** Exclusive. */
  end: number;
}

export type MentionValues = Record<MentionCategory, string[]>;

export const EMPTY_MENTION_VALUES: MentionValues = {
  practitioner: [],
  product: [],
  city: [],
};

/**
 * Singular/plural per category, as an explicit map.
 *
 * Never derive this by appending 's' -- "city" would become "citys".
 */
const CATEGORY_LABELS: Record<MentionCategory, [string, string]> = {
  practitioner: ['practitioner', 'practitioners'],
  product: ['product', 'products'],
  city: ['city', 'cities'],
};

export function categoryLabel(category: MentionCategory, count: number): string {
  const [singular, plural] = CATEGORY_LABELS[category];
  return count === 1 ? singular : plural;
}

/**
 * A mention's usable values: non-empty, de-duplicated, order preserved.
 *
 * Both mentionTokenText and buildMentionQuery go through this, so the token the
 * user sees and the text the agent receives can never disagree about how many
 * values a mention holds.
 */
export function mentionValues(mention: Mention): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of mention.values || []) {
    if (typeof value !== 'string') continue;
    if (!value.trim()) continue;
    if (seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  return out;
}

/**
 * The literal text a mention occupies in the composer.
 *
 * "@John Smith +1" rather than joining values with a comma, because stored
 * names already contain commas ("Gray, Aaron M.") -- a joined label would be
 * ambiguous to read and impossible to validate. Because this is a pure function
 * of the values, "has the user hand-edited this token?" is one string compare.
 */
export function mentionTokenText(mention: Mention): string {
  const values = mentionValues(mention);
  if (values.length === 0) return '';
  if (values.length === 1) return `@${values[0]}`;
  return `@${values[0]} +${values.length - 1}`;
}

/**
 * Find where each mention's token currently sits, by scanning left to right.
 *
 * Deriving positions instead of storing them is what removes an entire offset-
 * diffing layer: typing before a token, typing after it, pasting over a range,
 * and select-all-replace all fall out of this one scan for free. A mention whose
 * token is no longer present -- because the user edited inside it -- is simply
 * dropped, which is the graceful degradation we want (the text stays exactly as
 * typed and is sent as plain prose).
 *
 * Each mention claims the first occurrence of its token that another mention
 * has not already taken. That does two things at once: mentions arrive in
 * SELECTION order, which is not the same as positional order once the user goes
 * back and inserts one earlier in the sentence, and two mentions whose tokens
 * are textually identical still resolve to different occurrences rather than
 * both landing on the first.
 *
 * The result is sorted by position, so callers can splice straight through it.
 */
export function locateMentions(text: string, mentions: Mention[]): LocatedMention[] {
  const located: LocatedMention[] = [];

  for (const mention of mentions || []) {
    const token = mentionTokenText(mention);
    if (!token) continue;

    let from = 0;
    for (;;) {
      const start = text.indexOf(token, from);
      if (start === -1) break;

      const end = start + token.length;
      const taken = located.some((entry) => start < entry.end && end > entry.start);
      if (!taken) {
        located.push({ mention, start, end });
        break;
      }
      from = start + 1;
    }
  }

  located.sort((a, b) => a.start - b.start);
  return located;
}

/**
 * Quote a value for the bracketed list.
 *
 * Backslash is escaped BEFORE the apostrophe, or the escape we add would itself
 * get escaped. Names like "O'Brien" are common enough that this matters.
 */
export function quoteValue(value: string): string {
  return `'${value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
}

/**
 * Rewrite the composed text into the message the agent receives.
 *
 *   "Did @John Smith +1 purchase @Vitamin D Supreme?"
 *     -> "Did practitioners ['John Smith', 'Smith, John M.'] purchase product ['Vitamin D Supreme']?"
 *
 * With no mentions this returns rawText unchanged, byte for byte -- every
 * message that does not use the picker is completely unaffected.
 *
 * Two mentions of the same category stay as two separate bracket groups. They
 * are two distinct constraints in the sentence, and merging them would change
 * the question being asked.
 */
export function buildMentionQuery(rawText: string, mentions: Mention[]): string {
  const located = locateMentions(rawText, mentions);
  if (located.length === 0) return rawText;

  let out = '';
  let cursor = 0;

  for (const { mention, start, end } of located) {
    // locateMentions scans forward, so this cannot overlap -- but a caller
    // passing hand-built data should not be able to corrupt the output.
    if (start < cursor) continue;

    const values = mentionValues(mention);
    const label = categoryLabel(mention.category, values.length);
    const list = values.map(quoteValue).join(', ');

    out += rawText.slice(cursor, start);
    out += `${label} [${list}]`;
    cursor = end;
  }

  return out + rawText.slice(cursor);
}

/* ------------------------------------------------------------------ *
 * Matching
 * ------------------------------------------------------------------ */

/**
 * Fold away the differences that should not affect a match: accents, case, and
 * runs of whitespace. Deliberately does NOT strip punctuation -- the comma in
 * "Gray, Aaron M." is a token separator handled below, not noise.
 */
export function normalizeForMatch(value: string): string {
  return (value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Lax substring match: every token in the query must appear somewhere in the
 * candidate.
 *
 * This is what makes name order stop mattering. "john" finds both "John Smith"
 * and "Gray, John M."; "john smith" finds "John Smith" and "Smith, John M."
 * alike. No fuzzy or edit-distance scoring -- a typo finds nothing, which is
 * predictable and keeps near-misses out of the list.
 */
export function matchesQuery(candidate: string, query: string): boolean {
  const haystack = normalizeForMatch(candidate);
  if (!haystack) return false;

  const tokens = normalizeForMatch(query).split(/[\s,]+/).filter(Boolean);
  if (tokens.length === 0) return true;

  return tokens.every((token) => haystack.includes(token));
}

/**
 * Matches for one category, capped for display.
 *
 * `total` is the true match count, not the capped length, so the panel can
 * offer "+N more" honestly.
 */
export function filterMentionCandidates(
  values: string[],
  query: string,
  cap: number,
): { items: string[]; total: number } {
  const items: string[] = [];
  let total = 0;

  for (const value of values || []) {
    if (!value || !value.trim()) continue;
    if (!matchesQuery(value, query)) continue;
    total += 1;
    if (items.length < cap) items.push(value);
  }

  return { items, total };
}

/* ------------------------------------------------------------------ *
 * Text operations
 * ------------------------------------------------------------------ */

/**
 * Should typing "@" at this index open the panel?
 *
 * Only at the start of the text or after whitespace or an opening bracket, so
 * an email address ("vk@dfh.com") never triggers it.
 */
export function canOpenPanelAt(text: string, atIndex: number): boolean {
  if (atIndex <= 0) return atIndex === 0;
  return /[\s([]/.test(text.charAt(atIndex - 1));
}

/**
 * Where an edit put a new "@" that should open the panel, or -1.
 *
 * An edit is not always one character. Paste, key autorepeat, mobile
 * autocomplete and IME commits can all deliver several at once, so checking
 * only the character before the caret misses "@a" arriving together -- the
 * panel would open on the "@" with an EMPTY query and never see the "a",
 * showing every value instead of the matches.
 *
 * Only "@"s inside the inserted region count. Scanning the whole text would
 * re-open the panel on an "@" the user typed earlier and dismissed.
 */
export function findMentionTrigger(prev: string, next: string, caret: number): number {
  if (next.length <= prev.length) return -1;

  // The inserted region, as a common-prefix / common-suffix diff.
  const shortest = Math.min(prev.length, next.length);
  let start = 0;
  while (start < shortest && prev.charAt(start) === next.charAt(start)) start += 1;

  let tailShared = 0;
  while (
    tailShared < shortest - start
    && prev.charAt(prev.length - 1 - tailShared) === next.charAt(next.length - 1 - tailShared)
  ) {
    tailShared += 1;
  }
  const insertedEnd = next.length - tailShared;

  // The last qualifying "@" at or before the caret wins, so pasting
  // "ask @bob about @sue" anchors on the one being typed into.
  for (let i = Math.min(insertedEnd, caret) - 1; i >= start; i -= 1) {
    if (next.charAt(i) !== '@') continue;
    if (!canOpenPanelAt(next, i)) continue;
    // A newline ends a mention, so an "@" before one is not in play.
    if (next.slice(i + 1, caret).indexOf('\n') !== -1) return -1;
    return i;
  }

  return -1;
}

/**
 * Replace the in-progress "@query" with a resolved token.
 *
 * `anchor` is the index of the "@"; `queryLength` is how many characters the
 * user typed after it. Returns the new text and where the caret should land.
 */
export function insertMention(
  text: string,
  anchor: number,
  queryLength: number,
  mention: Mention,
): { text: string; caret: number } {
  const token = mentionTokenText(mention);
  const replaceEnd = Math.min(text.length, anchor + 1 + queryLength);
  const next = text.slice(0, anchor) + token + text.slice(replaceEnd);
  return { text: next, caret: anchor + token.length };
}

/** The located mention whose token ends exactly at `caret`, if any. */
export function mentionEndingAt(located: LocatedMention[], caret: number): LocatedMention | null {
  return located.find((entry) => entry.end === caret) || null;
}

/** The located mention whose token starts exactly at `caret`, if any. */
export function mentionStartingAt(located: LocatedMention[], caret: number): LocatedMention | null {
  return located.find((entry) => entry.start === caret) || null;
}

/**
 * Remove a whole token at once, so Backspace at its edge deletes the mention
 * rather than nibbling one character off the end and invalidating it.
 */
export function removeMentionFromText(
  text: string,
  located: LocatedMention,
  mentions: Mention[],
): { text: string; caret: number; mentions: Mention[] } {
  return {
    text: text.slice(0, located.start) + text.slice(located.end),
    caret: located.start,
    mentions: (mentions || []).filter((m) => m.id !== located.mention.id),
  };
}

/**
 * Drop mentions whose tokens no longer appear in the text.
 *
 * Call this after every edit so state cannot drift from what is on screen.
 */
export function pruneMentions(text: string, mentions: Mention[]): Mention[] {
  const surviving = new Set(locateMentions(text, mentions).map((entry) => entry.mention.id));
  return (mentions || []).filter((m) => surviving.has(m.id));
}
